import { useEffect, useMemo, useRef } from 'react'

import { debounce } from 'lodash'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { useToggleCatalogPermissionModel } from '@webapp/modules/purchasing/features/toggle-catalog-permission/models'
import messages from './messages'

interface UseToggleCatalogPermissionPresenterOptions {
  onChange: (next: boolean) => void
}

export const useToggleCatalogPermissionPresenter = ({
  onChange,
}: UseToggleCatalogPermissionPresenterOptions) => {
  const { mutate, isLoading } = useToggleCatalogPermissionModel()
  const intl = useProcurifyIntl()

  // The debounced toggle is created once; keep it reading the latest onChange.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  })

  const toggle = useMemo(() => {
    // Flips are applied optimistically so the controlled Toggle moves on
    // click; the saves coalesce under the debounce. `priorValue` remembers
    // the state before the current burst of flips, so a failed save reverts
    // to real server truth even when the burst ended back where it started.
    let priorValue: boolean | undefined
    const save = debounce((value: boolean) => {
      const revertValue = priorValue ?? !value
      priorValue = undefined
      mutate(value, {
        onSuccess: () => {
          toast.success({
            title: intl.formatMessage(messages.successTitle),
            children: intl.formatMessage(messages.successMessage),
          })
        },
        onError: (error) => {
          onChangeRef.current(revertValue)
          toast.error({
            title: intl.formatMessage(messages.errorTitle),
            children:
              getMessageFromError(error) ||
              intl.formatMessage(messages.errorMessage),
          })
        },
      })
    }, 500)
    return Object.assign(
      (value: boolean) => {
        if (priorValue === undefined) {
          priorValue = !value
        }
        onChangeRef.current(value)
        save(value)
      },
      { cancel: save.cancel }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => () => toggle.cancel(), [toggle])

  return {
    commands: {
      toggle,
    },
    states: {
      isSaving: isLoading,
    },
  }
}
